import { useMemo, useState } from 'react';
import styled from 'astroturf/react';
import { TextButton } from 'js/design_system/TextButton';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Body } from 'js/design_system/Body';
import { RelatedClaim, WorkClaim } from './types';
import WorkMetadata from './WorkMetadata';
import PaperThumbnail from './PaperThumbnail';
import trackClick from 'js/utils/trackClick';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

const COLLAPSED_ENTRIES_COUNT = 4;

type ClaimsListProps = {
  sourceClaim: WorkClaim;
  title: string;
  entries: RelatedClaim[];
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs-16, 16px);
  margin: var(--spacing-md-48, 48px) 0;

  &:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 767px) {
    margin: 0;
  }
`;

const Card = styled.div`
  background: var(--background-white, #fff);
  border: 1px solid var(--stroke-light, #dddde2);
  border-radius: var(--corner-radius-radius-lg, 16px);
  overflow: hidden;
  padding: var(--spacing-sm-32, 32px);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm-32, 32px);
`;

const ListEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ListEntryPaperRow = styled.div`
  display: flex;
  gap: var(--spacing-xs-16, 16px);
  align-items: flex-start;
`;

const ShowMoreButton = styled(TextButton)`
  color: var(--text-primary, #141413);
  font-weight: var(--type-font-weight-regular);
  gap: var(--spacing-xs-4, 4px);
`;

const ShowMoreButtonText = styled.span`
  text-decoration: underline;
`;

const ShowMoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const SourceCopy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ClaimsList: React.FunctionComponent<ClaimsListProps> = ({
  sourceClaim,
  title,
  entries,
}) => {
  const [expanded, setExpanded] = useState(false);
  const visible = useMemo(
    () => (expanded ? entries : entries.slice(0, COLLAPSED_ENTRIES_COUNT)),
    [entries, expanded],
  );
  const showMore = entries.length > COLLAPSED_ENTRIES_COUNT && !expanded;

  return (
    <Container>
      <Body bold size="lg" as="h2">
        {title} ({entries.length})
      </Body>
      <Card>
        {visible.map((entry, i) => (
          <ListEntry key={`claim-${i}`}>
            <BodyLink
              size="md"
              href={entry.claim_url}
              rel="nofollow"
              onClick={() => {
                trackClick({
                  source: 'single_claim_page.claims_list.entry.click',
                  extra: {
                    list_type: title,
                    entry_id: entry.id,
                    source_claim_id: sourceClaim.id,
                  },
                });
              }}
            >
              {entry.claim_text}
            </BodyLink>
            <ListEntryPaperRow>
              <PaperThumbnail
                work={entry.work}
                width={41}
                height={60}
                paperType={title}
              />
              <SourceCopy>
                <BodyLink
                  size="md"
                  href={entry.work.url}
                  onClick={() => {
                    trackClick({
                      source: 'single_claim_page.claims_list.entry.title.click',
                      extra: {
                        list_type: title,
                        entry_id: entry.id,
                        source_claim_id: sourceClaim.id,
                      },
                    });
                  }}
                >
                  {entry.work.title}
                </BodyLink>
                <WorkMetadata
                  sourceClaim={sourceClaim}
                  work={entry.work}
                  metadataLocation={title}
                  showTitle={false}
                />
              </SourceCopy>
            </ListEntryPaperRow>
          </ListEntry>
        ))}
        {showMore && (
          <ImpressionWatcher
            source="single_claim_page.claims_list.show_more_button"
            extra={{
              source_claim_id: sourceClaim.id,
              list_type: title,
            }}
          >
            <ShowMoreRow>
              <ShowMoreButton
                type="button"
                inline
                iconRight="expand_more"
                onClick={() => {
                  trackClick({
                    source: 'single_claim_page.claims_list.show_more',
                    extra: {
                      source_claim_id: sourceClaim.id,
                      list_type: title,
                    },
                  });
                  setExpanded(true);
                }}
              >
                <ShowMoreButtonText>Show more</ShowMoreButtonText>
              </ShowMoreButton>
            </ShowMoreRow>
          </ImpressionWatcher>
        )}
      </Card>
    </Container>
  );
};

export default ClaimsList;
